import { Button, Popconfirm, message, Table, Input } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAiProfiles,
  deleteAiProfile,
  AiProfile,
} from "@/lib/api/resources/agents";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

export default function AiProfilesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const handleNavigate = (path: string) => {
    navigate(path, { state: { fromSearch: location.search } });
  };

  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const { data: profilesResponse, isLoading } = useQuery({
    queryKey: ["ai_profiles"],
    queryFn: fetchAiProfiles,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAiProfile(id),
    onSuccess: () => {
      message.success("Profile deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["ai_profiles"] });
    },
    onError: () => {
      message.error("Failed to delete profile");
    },
  });

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteMutation.mutate(id);
  };

  const searchQuery = searchParams.get("search") || "";
  const sortField = searchParams.get("sortField");
  const sortOrder = searchParams.get("sortOrder") as
    | "ascend"
    | "descend"
    | null;
  const filterProviders = searchParams.getAll("filterProvider");

  const rawDataSource = Array.isArray(profilesResponse)
    ? profilesResponse
    : (profilesResponse as any)?.member ||
      (profilesResponse as any)?.["hydra:member"] ||
      [];

  const uniqueProviders = Array.from(
    new Set(
      rawDataSource.map(
        (item: AiProfile) => item.aiConnection?.provider || "-",
      ),
    ),
  ).map((provider) => ({
    text: provider as string,
    value: provider as string,
  }));

  const dataSource = rawDataSource.filter((item: AiProfile) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = (item.name || "").toLowerCase().includes(q);
    const providerMatch = (item.aiConnection?.provider || "")
      .toLowerCase()
      .includes(q);
    const modelMatch = (item.aiConnection?.model || "")
      .toLowerCase()
      .includes(q);
    return nameMatch || providerMatch || modelMatch;
  });

  const columns: any = [
    {
      title: "Profile Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: AiProfile, b: AiProfile) =>
        (a.name || "").localeCompare(b.name || ""),
      sortOrder: sortField === "name" ? sortOrder : null,
      render: (text: string, record: AiProfile) => {
        return (
          <div className="flex items-center gap-3 py-2">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-cyan-500 to-blue-500 flex items-center justify-center shadow-md text-white shrink-0">
              <ApiOutlined className="text-lg opacity-90" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800 text-sm tracking-tight">
                {text || "Unknown Profile"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Provider",
      key: "provider",
      filters: uniqueProviders,
      filteredValue: filterProviders.length > 0 ? filterProviders : null,
      onFilter: (value: any, record: AiProfile) =>
        (record.aiConnection?.provider || "-") === value,
      sorter: (a: AiProfile, b: AiProfile) =>
        (a.aiConnection?.provider || "").localeCompare(
          b.aiConnection?.provider || "",
        ),
      sortOrder: sortField === "provider" ? sortOrder : null,
      render: (_: any, record: AiProfile) => {
        const provider = record.aiConnection?.provider || "-";
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200">
            {provider}
          </span>
        );
      },
    },
    {
      title: "Model",
      key: "model",
      sorter: (a: AiProfile, b: AiProfile) =>
        (a.aiConnection?.model || "").localeCompare(
          b.aiConnection?.model || "",
        ),
      sortOrder: sortField === "model" ? sortOrder : null,
      render: (_: any, record: AiProfile) => {
        const model = record.aiConnection?.model || "-";
        return (
          <span className="text-sm text-slate-600 font-medium">{model}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: AiProfile) => (
        <div className="flex gap-2">
          <Button
            type="text"
            className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate(`/ai-profiles/edit/${record.id}`);
            }}
          />
          <Popconfirm
            title="Delete this profile?"
            description="Are you sure you want to delete this AI profile?"
            onConfirm={(e) =>
              handleDelete(e as unknown as React.MouseEvent, record.id)
            }
            okText="Yes, delete it"
            cancelText="No, cancel"
            okButtonProps={{ danger: true, className: "bg-red-500" }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-red-600 hover:bg-red-50"
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-end items-end">
        <div className="flex gap-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleNavigate("/ai-profiles/create")}
            size="large"
            className="bg-slate-800 hover:bg-slate-700 border-none shadow-sm shadow-slate-200"
          >
            Add Profile
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
          <Input
            placeholder="Search profiles by name, provider, or model..."
            prefix={<SearchOutlined className="text-slate-400" />}
            className="max-w-md rounded-lg"
            defaultValue={searchQuery}
            onChange={(e) => {
              const val = e.target.value;
              setSearchParams(
                (prev) => {
                  if (val) {
                    prev.set("search", val);
                    prev.set("page", "1"); // Reset page on search
                  } else {
                    prev.delete("search");
                  }
                  return prev;
                },
                { replace: true },
              );
            }}
            allowClear
          />
        </div>
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: 15,
          }}
          onChange={(pagination, filters: any, sorter: any) => {
            setSearchParams((prev) => {
              prev.set("page", pagination.current?.toString() || "1");

              if (sorter && sorter.columnKey && sorter.order) {
                prev.set("sortField", sorter.columnKey);
                prev.set("sortOrder", sorter.order);
              } else {
                prev.delete("sortField");
                prev.delete("sortOrder");
              }

              if (filters.provider && filters.provider.length > 0) {
                prev.delete("filterProvider");
                filters.provider.forEach((f: any) =>
                  prev.append("filterProvider", f.toString()),
                );
              } else {
                prev.delete("filterProvider");
              }

              return prev;
            });
          }}
          onRow={(record) => ({
            onClick: () => handleNavigate(`/ai-profiles/edit/${record.id}`),
            className: "cursor-pointer hover:bg-slate-50 transition-colors",
          })}
        />
      </div>
    </div>
  );
}
